/**
 * Guest session: a temporary token for one QR visit. No account, no password, no refresh. The token is
 * scoped to a tenant, branch and dining session by the backend; the QR token is remembered next to it so a
 * scan of a different QR never reuses it.
 */
import type { JoinSessionResult } from '@/api/types';
import { isLocationType } from '@/venue/venue';
import type { LocationType } from '@/venue/venue';
import { decodeJwtClaims, jwtExpiryMs } from './jwt';
import { createSessionStore } from './session-store';

export interface GuestSession {
  /** Bearer token. Epoch-millisecond expiry is read from its `exp` claim. */
  token: string;
  expiresAt: number | null;
  sessionId: string;
  tenantId: string;
  branchId: string;
  locationId: string;
  locationLabel: string;
  locationType: LocationType;
  /** The QR token this session was started from. */
  qrToken: string;
  displayName: string;
  partySize: number;
}

const isString = (value: unknown): value is string => typeof value === 'string' && value !== '';

function parseGuestSession(raw: unknown): GuestSession | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  if (
    !isString(v.token) ||
    !isString(v.sessionId) ||
    !isString(v.tenantId) ||
    !isString(v.branchId) ||
    !isString(v.locationId) ||
    !isString(v.locationLabel) ||
    !isLocationType(v.locationType) ||
    !isString(v.qrToken) ||
    !isString(v.displayName) ||
    typeof v.partySize !== 'number' ||
    (v.expiresAt !== null && typeof v.expiresAt !== 'number')
  ) {
    return null;
  }
  return {
    token: v.token,
    expiresAt: v.expiresAt,
    sessionId: v.sessionId,
    tenantId: v.tenantId,
    branchId: v.branchId,
    locationId: v.locationId,
    locationLabel: v.locationLabel,
    locationType: v.locationType,
    qrToken: v.qrToken,
    displayName: v.displayName,
    partySize: v.partySize,
  };
}

export const guestSessionStore = createSessionStore<GuestSession>({
  key: 'rp.guest-session.v1',
  parse: parseGuestSession,
});

/** Builds a session from a join response, or null if the token is not a usable session token. */
export function guestSessionFromJoin(input: {
  qrToken: string;
  displayName: string;
  partySize: number;
  result: JoinSessionResult;
}): GuestSession | null {
  const { result } = input;
  const claims = decodeJwtClaims(result.session_token);
  if (!claims || !isString(claims.tenant_id) || !isString(claims.branch_id)) return null;
  if (!isString(result.session_id) || !isLocationType(result.location_type)) return null;

  return {
    token: result.session_token,
    expiresAt: jwtExpiryMs(result.session_token),
    sessionId: result.session_id,
    tenantId: claims.tenant_id,
    branchId: claims.branch_id,
    locationId: result.location_id,
    locationLabel: result.location_label,
    locationType: result.location_type,
    qrToken: input.qrToken,
    displayName: input.displayName,
    partySize: input.partySize,
  };
}

/** The stored session, but only when it was started from this QR token. */
export function getGuestSessionFor(qrToken: string): GuestSession | null {
  const session = guestSessionStore.get();
  return session && session.qrToken === qrToken ? session : null;
}
